import { useNotification } from 'app/providers'
import { Button } from 'common/components/Button/Button'
import { Modal } from 'common/components/Modal'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import {
  BUTTON_MODIFIER,
  BUTTON_PROPORTION,
  BUTTON_SEVERITY,
} from 'common/constants/buttonConstants'
import { IMAGE_CAPTURE_SETTINGS } from 'common/constants/imageCaptureConstants'
import { useAppDispatch } from 'common/hooks/redux'
import type { EncounterPhotoApiItem } from 'features/Procedure/state/procedure-api.type'
import { procedureApi } from 'features/Procedure/state/procedure.api'
import { ProcedureStoreTagTypes } from 'features/Procedure/state/procedure.type'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router'
import { createEncounterPhotoUploadFailureError, showPersistentPhotoError } from './photos.helpers'
import {
  addPhotoActionBreadcrumb,
  addPhotoUploadBreadcrumb,
  capturePhotoActionMessage,
  capturePhotoUploadError,
  capturePhotoUploadWarning,
} from './photos.sentry'
import type { PhotoData } from './photos.types'
import styles from './procedurePictureModal.module.scss'
import { useEncounterPhotoUpload } from './useEncounterPhotoUpload'

type CapturedPhoto = {
  blob: Blob
  previewUrl: string
}

const {
  MAX_CAPTURE_DIMENSION,
  JPEG_QUALITY: CAPTURE_JPEG_QUALITY,
  CAMERA_IDEAL_WIDTH,
  CAMERA_IDEAL_HEIGHT,
} = IMAGE_CAPTURE_SETTINGS

type PhotoCategory = 'before' | 'after'
type ModalUploadTaskContext = {
  category: PhotoCategory | null
  fileCount: number
}

interface ProcedurePictureModalProps {
  open: boolean
  photo: PhotoData | null | undefined
  category: PhotoCategory | null
  existingPhotos: EncounterPhotoApiItem[]
  onCancel: () => void
  onSubmit: (
    photoData: PhotoData | PhotoData[],
    options?: {
      closeModal?: boolean
    },
  ) => void
  isRetake?: boolean
  retakeFileName?: string | null
  selectedPatientId: number | null
  selectedEncounterUid: string | null
  onUploadTaskChange?: (task: Promise<unknown> | null, context?: ModalUploadTaskContext) => void
}

export const ProcedurePictureModal: React.FC<ProcedurePictureModalProps> = ({
  open,
  photo,
  category,
  existingPhotos,
  onCancel,
  onSubmit,
  isRetake = false,
  retakeFileName,
  selectedPatientId,
  selectedEncounterUid,
  onUploadTaskChange,
}) => {
  const dispatch = useAppDispatch()
  const { siteId } = useParams<{ siteId: string }>()

  const { setNotification } = useNotification()
  const [isUploading, setIsUploading] = useState(false)
  const [capturedPhotos, setCapturedPhotos] = useState<CapturedPhoto[]>([])
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0)
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isStartingCameraRef = useRef(false)
  const capturedPhotosRef = useRef<CapturedPhoto[]>([])
  const isSubmittingRef = useRef(false)
  const uploadEncounterPhotoFiles = useEncounterPhotoUpload()

  const updateCapturedPhotos = useCallback(
    (updater: (prev: CapturedPhoto[]) => CapturedPhoto[]) => {
      setCapturedPhotos((prev) => {
        const next = updater(prev)
        capturedPhotosRef.current = next
        return next
      })
    },
    [],
  )

  const clearRetryTimeout = useCallback(() => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current)
      retryTimeoutRef.current = null
    }
  }, [])

  const resetCanvas = useCallback(() => {
    if (!canvasRef.current) {
      return
    }

    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')

    if (ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
    }

    canvas.width = 0
    canvas.height = 0
  }, [])

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }

    if (videoRef.current) {
      videoRef.current.pause()
      videoRef.current.srcObject = null
    }
  }, [])

  const setCameraStarting = useCallback((isStarting: boolean) => {
    isStartingCameraRef.current = isStarting
  }, [])

  const resetModalState = useCallback(() => {
    clearRetryTimeout()
    stopCamera()
    resetCanvas()
    capturedPhotosRef.current.forEach((photo) => URL.revokeObjectURL(photo.previewUrl))
    updateCapturedPhotos(() => [])
    setCurrentPhotoIndex(0)
    setCameraStarting(false)
  }, [clearRetryTimeout, resetCanvas, setCameraStarting, stopCamera, updateCapturedPhotos])

  const handleModalClose = useCallback(() => {
    if (isUploading || isSubmittingRef.current) {
      addPhotoUploadBreadcrumb('Dialog close ignored: upload in progress', {
        category,
        isRetake,
        patientId: selectedPatientId,
        encounterUid: selectedEncounterUid,
        siteId,
      })
      return
    }

    resetModalState()
    onCancel()
  }, [
    category,
    isRetake,
    isUploading,
    onCancel,
    resetModalState,
    selectedEncounterUid,
    selectedPatientId,
    siteId,
  ])

  const startCamera = useCallback(async (): Promise<void> => {
    stopCamera()

    if (!videoRef.current) {
      throw new Error('Video element not available')
    }

    const video = videoRef.current

    try {
      let stream: MediaStream | null = null

      const videoConstraints: MediaTrackConstraints = {
        facingMode: 'environment',
        width: { ideal: CAMERA_IDEAL_WIDTH },
        height: { ideal: CAMERA_IDEAL_HEIGHT },
        frameRate: { ideal: 30 },
      }

      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: videoConstraints,
        })
      } catch {
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: { width: { ideal: CAMERA_IDEAL_WIDTH }, height: { ideal: CAMERA_IDEAL_HEIGHT } },
          })
        } catch {
          try {
            stream = await navigator.mediaDevices.getUserMedia({
              video: {
                facingMode: 'user',
                width: { ideal: CAMERA_IDEAL_WIDTH },
                height: { ideal: CAMERA_IDEAL_HEIGHT },
              },
            })
          } catch {
            throw new Error(
              'Unable to access camera. Please check permissions and ensure no other app is using the camera.',
            )
          }
        }
      }

      if (!stream) {
        throw new Error('Camera stream is null')
      }

      if (video) {
        video.srcObject = stream
        streamRef.current = stream

        if (video.readyState >= 2) {
          try {
            await video.play()
          } catch (playError) {
            console.error('Error playing video:', playError)
            throw playError
          }
        } else {
          await new Promise<void>((resolve, reject) => {
            const handleLoadedMetadata = async () => {
              video.removeEventListener('loadedmetadata', handleLoadedMetadata)
              try {
                await video.play()
                resolve()
              } catch (playError) {
                console.error('Error playing video after metadata loaded:', playError)
                reject(playError)
              }
            }
            video.addEventListener('loadedmetadata', handleLoadedMetadata)

            setTimeout(() => {
              video.removeEventListener('loadedmetadata', handleLoadedMetadata)
              reject(new Error('Video metadata loading timeout'))
            }, 5000)
          })
        }
      }
    } catch (error) {
      console.error('Camera error:', error)
      setNotification({
        type: ALERT_CONSTANTS.ERROR,
        duration: 2,
        title: 'Camera Error',
        description:
          error instanceof Error
            ? error.message
            : 'Failed to access camera. Please check permissions.',
      })
      throw error
    }
  }, [setNotification, stopCamera])

  useEffect(() => {
    if (open) {
      addPhotoUploadBreadcrumb('Dialog opened', {
        category,
        isRetake,
        hasExistingPhoto: !!photo,
        patientId: selectedPatientId,
        encounterUid: selectedEncounterUid,
        siteId,
      })
    } else {
      addPhotoUploadBreadcrumb('Dialog closed')
      resetModalState()
    }
  }, [
    open,
    resetModalState,
    category,
    isRetake,
    photo,
    selectedPatientId,
    selectedEncounterUid,
    siteId,
  ])

  useEffect(() => {
    return () => {
      clearRetryTimeout()
      stopCamera()
      resetCanvas()
      capturedPhotosRef.current.forEach((photo) => URL.revokeObjectURL(photo.previewUrl))
      capturedPhotosRef.current = []
    }
  }, [clearRetryTimeout, resetCanvas, stopCamera])

  useEffect(() => {
    if (!open) {
      return
    }

    const shouldShowCamera =
      !photo && (capturedPhotos.length === 0 || currentPhotoIndex >= capturedPhotos.length)

    if (!shouldShowCamera) {
      clearRetryTimeout()
      stopCamera()
      setCameraStarting(false)
      return
    }

    if (isStartingCameraRef.current) {
      return
    }

    let isDisposed = false

    const checkAndStart = async (retryCount = 0) => {
      if (
        isDisposed ||
        !open ||
        photo ||
        (capturedPhotos.length > 0 && currentPhotoIndex < capturedPhotos.length)
      ) {
        setCameraStarting(false)
        return
      }

      if (retryCount > 20) {
        setCameraStarting(false)
        setNotification({
          type: ALERT_CONSTANTS.ERROR,
          duration: 2,
          title: 'Camera Error',
          description:
            'Camera could not be initialized. Please try closing and reopening the modal.',
        })
        return
      }

      if (videoRef.current && videoRef.current.isConnected) {
        try {
          await startCamera()
        } catch (error) {
          console.error('Failed to start camera:', error)
        } finally {
          if (!isDisposed) {
            setCameraStarting(false)
          }
        }
        return
      }

      retryTimeoutRef.current = setTimeout(() => {
        void checkAndStart(retryCount + 1)
      }, 150)
    }

    setCameraStarting(true)
    const startTimer = setTimeout(() => {
      void checkAndStart(0)
    }, 200)

    return () => {
      isDisposed = true
      clearTimeout(startTimer)
      clearRetryTimeout()
      stopCamera()
      setCameraStarting(false)
    }
  }, [
    capturedPhotos.length,
    clearRetryTimeout,
    currentPhotoIndex,
    open,
    photo,
    setCameraStarting,
    setNotification,
    startCamera,
    stopCamera,
  ])

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return

    const video = videoRef.current
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')

    if (!ctx) return

    let width = video.videoWidth
    let height = video.videoHeight

    if (width === 0 || height === 0) {
      capturePhotoUploadWarning(
        'capturePhoto: video dimensions are zero',
        { category, patientId: selectedPatientId, encounterUid: selectedEncounterUid },
        { videoWidth: width, videoHeight: height },
      )
      return
    }

    if (width > MAX_CAPTURE_DIMENSION || height > MAX_CAPTURE_DIMENSION) {
      const scale = MAX_CAPTURE_DIMENSION / Math.max(width, height)
      width = Math.round(width * scale)
      height = Math.round(height * scale)
    }

    canvas.width = width
    canvas.height = height
    ctx.drawImage(video, 0, 0, width, height)

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          capturePhotoUploadWarning(
            'capturePhoto: canvas.toBlob produced null blob',
            { category, patientId: selectedPatientId, encounterUid: selectedEncounterUid },
            { canvasWidth: width, canvasHeight: height },
          )
          return
        }
        const previewUrl = URL.createObjectURL(blob)
        addPhotoUploadBreadcrumb('Photo captured', {
          blobSize: blob.size,
          width,
          height,
        })
        updateCapturedPhotos((prev) => {
          const next = [...prev, { blob, previewUrl }]
          setCurrentPhotoIndex(next.length - 1)
          return next
        })
      },
      'image/jpeg',
      CAPTURE_JPEG_QUALITY,
    )
  }

  const handleRetake = () => {
    const sentryContext = {
      category,
      patientId: selectedPatientId,
      encounterUid: selectedEncounterUid,
      siteId,
      capturedPhotosCount: capturedPhotos.length,
      isRetake,
      photoId: photo?.id ?? null,
      photoFileName: retakeFileName ?? photo?.label ?? null,
    }

    if (capturedPhotos.length === 0) {
      capturePhotoActionMessage(
        'Retake ignored: no captured photos available',
        sentryContext,
        {
          source: 'procedure-picture-modal',
          currentPhotoIndex,
        },
        'warning',
      )
      return
    }

    capturePhotoActionMessage('Retake requested from capture modal', sentryContext, {
      source: 'procedure-picture-modal',
      currentPhotoIndex,
    })

    const removed = capturedPhotos[currentPhotoIndex]
    if (removed) {
      URL.revokeObjectURL(removed.previewUrl)
    }
    const newPhotos = capturedPhotos.filter((_, i) => i !== currentPhotoIndex)
    addPhotoActionBreadcrumb('Captured photo discarded for retake', sentryContext, {
      source: 'procedure-picture-modal',
      removedPhotoIndex: currentPhotoIndex,
      remainingPhotosCount: newPhotos.length,
    })
    updateCapturedPhotos(() => newPhotos)
    setCurrentPhotoIndex(newPhotos.length)
  }

  const handleNext = () => {
    if (currentPhotoIndex < capturedPhotos.length - 1) {
      setCurrentPhotoIndex(currentPhotoIndex + 1)
    } else {
      setCurrentPhotoIndex(capturedPhotos.length)
    }
  }

  const handleCancelCapture = () => {
    if (isUploading || isSubmittingRef.current) {
      return
    }

    if (capturedPhotos.length > 0) {
      setCurrentPhotoIndex(capturedPhotos.length - 1)
      return
    }

    handleModalClose()
  }

  const handleSubmit = async () => {
    // Double-click guard using ref (more reliable than state which is async)
    if (isSubmittingRef.current) {
      addPhotoUploadBreadcrumb(
        'Save ignored: already submitting (double-click)',
        undefined,
        'warning',
      )
      return
    }

    const sentryContext = {
      category,
      patientId: selectedPatientId,
      encounterUid: selectedEncounterUid,
      siteId,
      capturedPhotosCount: capturedPhotos.length,
      isRetake,
    }

    capturePhotoUploadWarning('Save button pressed', sentryContext, {
      hasCategoryProp: category !== null,
      hasPatientId: selectedPatientId !== null,
      hasEncounterUid: selectedEncounterUid !== null,
      hasSiteId: !!siteId,
    })

    if (!category || !selectedPatientId || !selectedEncounterUid || !siteId) {
      capturePhotoUploadWarning('Save aborted: missing required fields', sentryContext, {
        missingFields: [
          !category && 'category',
          !selectedPatientId && 'selectedPatientId',
          !selectedEncounterUid && 'selectedEncounterUid',
          !siteId && 'siteId',
        ].filter(Boolean),
      })
      setNotification({
        type: ALERT_CONSTANTS.ERROR,
        duration: 2,
        title: 'Error',
        description: 'Missing required information to upload photo',
      })
      return
    }

    if (capturedPhotos.length === 0) {
      capturePhotoUploadWarning('Save aborted: no captured photos', sentryContext, {
        hasPhotoProp: !!photo,
      })
      handleModalClose()
      return
    }

    isSubmittingRef.current = true
    setIsUploading(true)
    let hasShownUploadError = false

    const uploadTask = (async () => {
      try {
        addPhotoUploadBreadcrumb('Upload started: converting blobs to files', {
          fileCount: capturedPhotos.length,
          blobSizes: capturedPhotos.map((p) => p.blob.size),
        })

        const capturedFiles = capturedPhotos.map(
          (photo, index) => new File([photo.blob], `${index + 1}.jpg`, { type: 'image/jpeg' }),
        )

        addPhotoUploadBreadcrumb('Upload started: calling API', {
          fileCount: capturedFiles.length,
          fileSizes: capturedFiles.map((f) => f.size),
          category,
        })

        const { uploadedPhotos, failures } = await uploadEncounterPhotoFiles({
          category,
          files: capturedFiles,
          existingPhotos,
          siteId,
          patientId: selectedPatientId,
          encounterUid: selectedEncounterUid,
          overrideFileName: isRetake && retakeFileName ? retakeFileName : undefined,
        })

        capturePhotoUploadWarning('Upload completed', sentryContext, {
          uploadedCount: uploadedPhotos.length,
          failureCount: failures.length,
        })

        if (uploadedPhotos.length > 0) {
          dispatch(procedureApi.util.invalidateTags([ProcedureStoreTagTypes.ESignForm]))
          await onSubmit(uploadedPhotos.length === 1 ? uploadedPhotos[0] : uploadedPhotos, {
            closeModal: failures.length === 0,
          })
          addPhotoUploadBreadcrumb('Save succeeded: onSubmit called', {
            uploadedCount: uploadedPhotos.length,
            closeModal: failures.length === 0,
          })
        }

        if (failures.length > 0) {
          const failureDescriptions = failures.map((f) => ({
            order: f.order,
            fileName: f.sourceFileName,
            description: f.description,
          }))
          const partialFailureError = createEncounterPhotoUploadFailureError(
            failures,
            'Failed to upload photo(s).',
          )

          capturePhotoUploadWarning('Partial upload failure', sentryContext, {
            uploadedCount: uploadedPhotos.length,
            failureCount: failures.length,
            failures: failureDescriptions,
          })

          const failedOrders = new Set(failures.map((failure) => failure.order))
          capturedPhotos.forEach((capturedPhoto, index) => {
            if (!failedOrders.has(index)) {
              URL.revokeObjectURL(capturedPhoto.previewUrl)
            }
          })
          const remaining = capturedPhotos.filter((_, index) => failedOrders.has(index))
          updateCapturedPhotos(() => remaining)
          setCurrentPhotoIndex(0)
          showPersistentPhotoError(
            setNotification,
            'Upload failed',
            partialFailureError,
            'Failed to upload photo(s).',
          )
          hasShownUploadError = true
          throw partialFailureError
        }

        addPhotoUploadBreadcrumb('Save flow complete: resetting modal')
        resetModalState()
      } catch (error) {
        capturePhotoUploadError(error, sentryContext, {
          phase: 'handleSubmit',
        })
        if (!hasShownUploadError) {
          showPersistentPhotoError(
            setNotification,
            'Upload failed',
            error,
            'Failed to upload photo(s).',
          )
        }
        throw error
      }
    })()

    onUploadTaskChange?.(uploadTask, {
      category,
      fileCount: capturedPhotos.length,
    })

    try {
      await uploadTask
    } catch {
      // Error was already surfaced to the user and must stay rejected for leave-action waits.
    } finally {
      isSubmittingRef.current = false
      setIsUploading(false)
      onUploadTaskChange?.(null)
    }
  }

  const currentPhoto = capturedPhotos[currentPhotoIndex] || null
  const showCamera =
    (capturedPhotos.length === 0 && !photo) || currentPhotoIndex >= capturedPhotos.length

  return (
    <Modal
      open={open}
      onCancel={handleModalClose}
      title={null}
      keyboard
      maskClosable={false}
      footer={null}
      width={700}
      destroyOnClose
      wrapClassName={styles.modalWrapper}
      className={styles.modal}
      closeIcon={null}>
      <div className={styles.modalContent}>
        {showCamera ? (
          <>
            <div className={styles.cameraView}>
              <video ref={videoRef} autoPlay playsInline muted className={styles.videoPreview} />
              <div className={styles.cameraOverlay}>
                <Button
                  modifier={BUTTON_MODIFIER.SECONDARY}
                  severity={BUTTON_SEVERITY.DANGER}
                  proportion={BUTTON_PROPORTION.MEDIUM}
                  className={styles.cancelCameraButton}
                  onClick={handleCancelCapture}
                  disabled={isUploading}>
                  Cancel
                </Button>
                <button
                  className={styles.shutterButton}
                  onClick={capturePhoto}
                  disabled={isUploading}>
                  <span className={styles.shutterButtonInner} />
                </button>
              </div>
            </div>
            {!isRetake && capturedPhotos.length > 0 && (
              <div className={styles.existingThumbnails}>
                {capturedPhotos.map((photo, index) => (
                  <div key={`captured-${index}`} className={styles.thumbnail}>
                    <img src={photo.previewUrl} alt={`Captured ${index + 1}`} />
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className={styles.previewView}>
            {currentPhoto && (
              <>
                <div className={styles.mainImageContainer}>
                  <img src={currentPhoto.previewUrl} alt='Captured' className={styles.mainImage} />
                  <div className={styles.actionButtons}>
                    <Button
                      modifier={BUTTON_MODIFIER.SECONDARY}
                      severity={BUTTON_SEVERITY.DANGER}
                      proportion={BUTTON_PROPORTION.MEDIUM}
                      onClick={handleRetake}
                      disabled={isUploading}>
                      Retake
                    </Button>
                    {isRetake ? (
                      <Button
                        modifier={BUTTON_MODIFIER.PRIMARY}
                        severity={BUTTON_SEVERITY.SUCCESS_FILLED}
                        proportion={BUTTON_PROPORTION.MEDIUM}
                        onClick={handleSubmit}
                        disabled={isUploading}>
                        {isUploading ? 'Uploading...' : 'Save'}
                      </Button>
                    ) : (
                      <Button
                        modifier={BUTTON_MODIFIER.PRIMARY}
                        severity={BUTTON_SEVERITY.SUCCESS_FILLED}
                        proportion={BUTTON_PROPORTION.MEDIUM}
                        onClick={handleNext}
                        disabled={isUploading}>
                        Next
                      </Button>
                    )}
                  </div>
                </div>
                {!isRetake && (
                  <>
                    {capturedPhotos.length > 0 && (
                      <div className={styles.existingThumbnails}>
                        {capturedPhotos.map((photo, index) => (
                          <div
                            key={`captured-${index}`}
                            className={`${styles.thumbnail} ${
                              index === currentPhotoIndex ? styles.thumbnailActive : ''
                            }`}
                            onClick={() => setCurrentPhotoIndex(index)}>
                            <img src={photo.previewUrl} alt={`Thumbnail ${index + 1}`} />
                          </div>
                        ))}
                      </div>
                    )}
                    <div className={styles.footerButtons}>
                      <Button
                        modifier={BUTTON_MODIFIER.SECONDARY}
                        severity={BUTTON_SEVERITY.DANGER}
                        proportion={BUTTON_PROPORTION.MEDIUM}
                        onClick={handleModalClose}
                        disabled={isUploading}>
                        Cancel
                      </Button>
                      <Button
                        modifier={BUTTON_MODIFIER.PRIMARY}
                        proportion={BUTTON_PROPORTION.MEDIUM}
                        onClick={handleSubmit}
                        disabled={isUploading}>
                        {isUploading ? 'Uploading...' : 'Save'}
                      </Button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        )}
      </div>
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </Modal>
  )
}
